import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Aurora } from "@/components/motion/Aurora";

const KIND_LABEL = {
  deposit_hold: "Fianza preautorizada",
  deposit_charge: "Fianza cobrada",
  deposit_capture: "Captura de fianza",
  deposit_release: "Fianza liberada",
  deposit_refund: "Fianza devuelta",
  extras_charge: "Extras cobrados",
  post_rental_charge: "Cargo post-alquiler",
  hold_expired: "Hold expirado",
  dispute: "Disputa",
};

const KIND_COLOR = {
  deposit_charge: "text-forest-deep",
  deposit_capture: "text-forest-deep",
  extras_charge: "text-forest-deep",
  post_rental_charge: "text-forest-deep",
  deposit_refund: "text-danger",
  hold_expired: "text-danger",
  dispute: "text-danger",
};

export default function Finance() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: s }] = await Promise.all([
        api.get("/admin/ledger"),
        api.get("/admin/finance/summary"),
      ]);
      setRows(l.rows || []);
      setSummary(s);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const resp = await api.get("/admin/ledger/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ledger-campervan.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory pb-12">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-warmgray hover:text-forest-deep" data-testid="finance-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <Button onClick={exportCsv} variant="outline" className="border-forest-deep text-forest-deep" data-testid="finance-export-btn">
            <Download className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
        </div>
      </header>

      <main className="container-x py-8">
        <span className="text-overline">Conciliación</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-charcoal mt-1 mb-6">
          Finanzas
        </h1>

        {summary && (
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-sand rounded-xl p-5">
              <div className="text-overline mb-1">Total cobrado</div>
              <div className="font-display text-2xl font-bold text-forest-deep">
                {summary.total_charged.toFixed(2)} €
              </div>
            </div>
            <div className="bg-white border border-sand rounded-xl p-5">
              <div className="text-overline mb-1">Total devuelto</div>
              <div className="font-display text-2xl font-bold text-terracotta">
                {summary.total_refunded.toFixed(2)} €
              </div>
            </div>
            <div className="bg-white border border-sand rounded-xl p-5">
              <div className="text-overline mb-1">Neto</div>
              <div className="font-display text-2xl font-bold text-charcoal">
                {summary.net.toFixed(2)} €
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-sand rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-sand text-left text-xs uppercase tracking-wider text-warmgray-light">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Reserva</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Movimiento</th>
                <th className="px-4 py-3 text-right">Importe</th>
                <th className="px-4 py-3">Ref. Stripe</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-sand-light hover:bg-ivory/60">
                  <td className="px-4 py-3 text-warmgray whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-forest-deep bg-sand-light px-2 py-0.5 rounded">
                      {r.reservation_code}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-charcoal">{r.client_name}</td>
                  <td className="px-4 py-3">
                    <span className={KIND_COLOR[r.kind] || "text-warmgray"}>
                      {KIND_LABEL[r.kind] || r.kind}
                    </span>
                    {r.note && <div className="text-xs text-warmgray-light">{r.note}</div>}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${KIND_COLOR[r.kind] || ""}`}>
                    {Number(r.amount).toFixed(2)} €
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-warmgray-light max-w-[180px] truncate">
                    {r.stripe_ref || "—"}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-warmgray-light">Sin movimientos todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
